import { useQuery, UseQueryOptions } from "react-query";
import { FormField } from "@/v2/components/form/form-schema";
import {
  GetInteractionAdditionalContextResponse,
  InteractionAdditionalContextFieldsOptionsResponse,
} from "./InteractionAdditionalContext.api";
import { useInteractionAdditionalContextApi } from "./InteractionAdditionalContext.api";

type GetInteractionAdditionalContextKey = [
  "interaction-additional-context",
  "topics",
  number,
  "release-policies",
  string
];

export function getInteractionAdditionalContextKey(
  topicId: number,
  topicReleasePolicyId: string
): GetInteractionAdditionalContextKey {
  return [
    "interaction-additional-context",
    "topics",
    topicId,
    "release-policies",
    topicReleasePolicyId,
  ];
}

type UseGetInteractionAdditionalContextParams<
  TData = GetInteractionAdditionalContextResponse
> = {
  topicId: number;
  topicReleasePolicyId: string;
} & UseQueryOptions<
  GetInteractionAdditionalContextResponse,
  unknown,
  TData,
  GetInteractionAdditionalContextKey
>;

export function useGetInteractionAdditionalContext<
  TData = GetInteractionAdditionalContextResponse
>({
  topicId,
  topicReleasePolicyId,
  ...options
}: UseGetInteractionAdditionalContextParams<TData>) {
  const { get } = useInteractionAdditionalContextApi();

  return useQuery<
    GetInteractionAdditionalContextResponse,
    unknown,
    TData,
    GetInteractionAdditionalContextKey
  >({
    queryKey: getInteractionAdditionalContextKey(topicId, topicReleasePolicyId),
    queryFn: () =>
      get({
        topicId,
        topicReleasePolicyId: topicReleasePolicyId,
      }),
    ...options,
  });
}

export function getInteractionAdditionalContextSelector(
  data?: GetInteractionAdditionalContextResponse
) {
  return {
    customFields:
      data?.fields.filter(({ type }) => type === "customField") ?? [],
    enrichments: data?.fields.filter(({ type }) => type === "enrichment") ?? [],
    basicTicketData:
      data?.fields.filter(({ type }) => type === "basicTicketData") ?? [],
  };
}

type GetInteractionAdditionalContextFieldsOptionsKey = [
  "interaction-additional-context",
  "topics",
  number,
  "release-policies",
  string,
  "options"
];

type UseGetInteractionAdditionalContextFieldsOptionsParams<
  TData = InteractionAdditionalContextFieldsOptionsResponse
> = {
  topicId: number;
  topicReleasePolicyId: string;
} & UseQueryOptions<
  InteractionAdditionalContextFieldsOptionsResponse,
  unknown,
  TData,
  GetInteractionAdditionalContextFieldsOptionsKey
>;

export function useGetInteractionAdditionalContextFieldsOptions<
  TData = InteractionAdditionalContextFieldsOptionsResponse
>({
  topicId,
  topicReleasePolicyId,
  ...options
}: UseGetInteractionAdditionalContextFieldsOptionsParams<TData>) {
  const { getFieldsOptions } = useInteractionAdditionalContextApi();

  return useQuery<
    InteractionAdditionalContextFieldsOptionsResponse,
    unknown,
    TData,
    GetInteractionAdditionalContextFieldsOptionsKey
  >({
    queryKey: [
      "interaction-additional-context",
      "topics",
      topicId,
      "release-policies",
      topicReleasePolicyId,
      "options",
    ],
    queryFn: () => getFieldsOptions({ topicId, topicReleasePolicyId }),
    ...options,
  });
}

type GetQuackChatFieldOptionsKey = [
  "interaction-additional-context",
  "options",
  "quackchat"
];

export function useGetQuackChatFieldOptions(
  options?: UseQueryOptions<
    FormField[],
    unknown,
    FormField[],
    GetQuackChatFieldOptionsKey
  >
) {
  const { getQuackChatFieldOptions } = useInteractionAdditionalContextApi();

  return useQuery<FormField[], unknown, FormField[], GetQuackChatFieldOptionsKey>(
    {
      queryKey: ["interaction-additional-context", "options", "quackchat"],
      queryFn: getQuackChatFieldOptions,
      ...options,
    }
  );
}
